import "./App.css";
import { HashRouter, Route, Routes, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { useDispatch } from "react-redux";
import { ToastContainer } from "react-toastify";
import { ChakraProvider } from "@chakra-ui/react";
import "bootstrap/dist/css/bootstrap.min.css";

// Layouts (Keep these without lazy loading as they're core layouts)
import PublicLayout from "./components/defaultStructure/PublicLayout";
import AdminLayout from "./components/defaultStructure/AdminLayout";
import MyAccountLayout from "./components/defaultStructure/MyAccountLayout";

// Public routes components (Keep without lazy loading)
import Login from "./components/pages/Frontend/user_auth/login/Login";
import AdminLogin from "./components/adminLogin/AdminLogin";
import ForgetPassword from "./components/pages/Frontend/user_auth/login/Forgotpass";
import AdminForgotPassWord from "./components/pages/Frontend/user_auth/login/AdminForgotPassWord";
import CreatePassword from "./components/createPassword/CreatePassword";
import ScrollTop from "./components/pages/Frontend/ScrollTop";
import PaymentSuccess from "./components/pages/Frontend/shop/PaymentSuccess.jsx";
import QuotePublicDetail from "./components/pages/AdminPannel/crm/quote/QuotePublicDetail.jsx";
import NotFoundpage from "./components/defaultStructure/notFoundPage/NotFoundpage";

// Auth guards
import AdminPublicRoute from "./PublicRoute.jsx";
import AdminProtectedRoute from "./ProtectedRoute.jsx";
import PermissionProtectedRoute from "./components/router/ProtectedRoute.jsx";

// Redux actions
import { getWishlistProducts } from "./components/redux/reduxApi/website/WishlistApi.jsx";
import { Toaster } from "react-hot-toast";
import Shop from "./components/pages/Frontend/shop/Shop";
import AdminSingIn from "./components/pages/Frontend/user_auth/signup/AdminSingIn";
// Lazy load admin routes components

import Dashboard from "./components/pages/AdminPannel/dashboard/TaskDashboard.jsx";
import InventoryRoutes from "./routes/InventoryRoutes.jsx";
// const AddAddressForm = lazy(() => import("./components/pages/AdminPannel/customer/AddAddressForm"));
// const OrderList = lazy(() => import("./components/pages/AdminPannel/order/OrderList"));
// const DetailOrder = lazy(() => import("./components/pages/AdminPannel/order/DetailOrder"));
// const AddOrderForm = lazy(() => import("./components/pages/AdminPannel/order/AddOrderForm"));
const Order = lazy(() => import("./components/pages/AdminPannel/order/OrderTabs.jsx"));
const TagMaster = lazy(() => import("./components/pages/AdminPannel/tagmaster/TagMaster"));
const TagMasterForm = lazy(() => import("./components/pages/AdminPannel/tagmaster/TagMasterForm"));
const ProductReviewList = lazy(() => import("./components/pages/AdminPannel/productReview/ProductReviewList"));
const AuthPage = lazy(() => import("./components/pages/Frontend/user_auth/login/AuthPage"));
// const PrivacyList = lazy(() => import("./components/pages/AdminPannel/PrivacyPolicy/PrivacyList"));
const CrmRoutes = lazy(() => import("./routes/CrmRoutes"));
const MyAccountRoutes = lazy(() => import("./routes/MyAccountRoutes"));
const ProductionRoutes = lazy(() => import("./routes/ProductionRoutes"));
const SalesRoutes = lazy(() => import("./routes/SalesRoutes"));
const SettingsRoutes = lazy(() => import("./routes/SettingsRoutes"));
const SettingLayout = lazy(() => import("./components/pages/AdminPannel/settingsMaster/settingLayout"));
const Signup = lazy(() => import("./components/pages/AdminPannel/Auth/signup/Signup"));
// const BillingListview = lazy(() => import("./components/pages/AdminPannel/billing/BillingListview.jsx"));
// const BillingFormView = lazy(() => import("./components/pages/AdminPannel/billing/BillingFormView.jsx"));
// const BillingDetailview = lazy(() => import("./components/pages/AdminPannel/billing/BillingDetailview.jsx"));
// const ProductionOrderComponent = lazy(() => import("./components/pages/AdminPannel/production/productionOrder/ProductionOrder.jsx"));
const OrderConfirmation = lazy(() => import("./components/pages/Frontend/shop/OrderConfirmation.jsx"));

const Quoteemail = lazy(() => import("./components/pages/AdminPannel/crm/quote/Quoteemail.jsx"));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#666'
  }}>
    Loading...
  </div>
);

// Mock authentication check
const isAuthenticated = () => localStorage.getItem("role") !== null;
const getRole = () => localStorage.getItem("role");
const accountId = localStorage.getItem("UserRowId");

function App() {
  const dispatch = useDispatch();
  
  useEffect(() => {
    dispatch(getWishlistProducts(accountId));
  }, [dispatch, accountId]);

  return (
    <>
      <ToastContainer 
        position="top-left" 
        autoClose={2500} 
        hideProgressBar={false} 
        newestOnTop={false} 
        closeOnClick 
        rtl={false} 
        pauseOnFocusLoss 
        draggable 
        pauseOnHover 
        theme="light" 
      />

      <ChakraProvider>
        <HashRouter>
          <ScrollTop />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Layout */}
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/*" element={<PublicLayout />} />

              {/* Regular User Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgetPassword />} />
              <Route path="/create-password" element={<CreatePassword />} />
              <Route path="/*" element={<AdminLayout />} />
              <Route path="/admin-forgot-password" element={<AdminForgotPassWord />} />

              {/* Admin Auth Routes */}
              <Route path="/admin-login" element={
                <AdminPublicRoute>
                  <AdminLogin />
                </AdminPublicRoute>
              } />

              <Route path="/admin-forgot-password" element={
                <AdminPublicRoute>
                  <AdminForgotPassWord />
                </AdminPublicRoute>
              } />

              <Route path="/create-password" element={
                <AdminPublicRoute>
                  <CreatePassword />
                </AdminPublicRoute>
              } />

              {/* ADMIN PROTECTED ROUTES */}
              <Route path="/admin/*" element={
                <AdminProtectedRoute>
                  <AdminLayout />
                </AdminProtectedRoute>
              }>
                {/* Nested routes for admin */}
                <Route path="signup" element={<Signup />} />
                <Route path="dashboard" element={<Dashboard />} />
                {/* <Route path="quote-detail/:id" element={<QuotePublicDetail />} /> */}
                <Route path="quote-email" element={<Quoteemail />} />
                <Route path="crm/*" element={<CrmRoutes />} />
                <Route path="sales/*" element={<SalesRoutes />} />
                <Route path="production/*" element={<ProductionRoutes />} />
                <Route path="settings/*" element={<SettingsRoutes />} />
                <Route path="inventory/*" element={<InventoryRoutes/>} />
                
                <Route path="tag-master" element={
                  <PermissionProtectedRoute moduleName="Tag Master" action="viewOnly">
                    <TagMaster />
                  </PermissionProtectedRoute>
                } />

                <Route path="tag-master-form" element={
                  <PermissionProtectedRoute moduleName="Tag Master" action="add">
                    <TagMasterForm />
                  </PermissionProtectedRoute>
                } />

                {/* Bill */}
                {/* <Route path="Billing-list" element={
                  <PermissionProtectedRoute moduleName="Bill" action="viewOnly">
                    <BillingListview />
                  </PermissionProtectedRoute>
                } />
                <Route path="Billing-Form" element={
                  <PermissionProtectedRoute moduleName="Bill" action="add">
                    <BillingFormView />
                  </PermissionProtectedRoute>
                } />
                <Route path="billing-detail" element={
                  <PermissionProtectedRoute moduleName="Bill" action="viewAll">
                    <BillingDetailview />
                  </PermissionProtectedRoute>
                } /> */}

                {/* PORTAL SETTINGS */}
                <Route path="organisation-settings" element={
                  <PermissionProtectedRoute
                    moduleName={[
                      "organisation profile",
                      "roles",
                      "column formulation",
                      "zip code master",
                      "terms and condition",
                      "warehouse",
                      "colour",
                      "motorization",
                      "volumetric",
                      "material collection",
                    ]}
                    action="viewOnly"
                  >
                    <SettingLayout />
                  </PermissionProtectedRoute>
                } />

                <Route path="*" element={<NotFoundpage />} />
              </Route>

              {/* My Account Layout - FIXED NESTED ROUTING */}
              <Route path="/account/*" element={
                <MyAccountLayout />
              }>
                <Route path="*" element={<MyAccountRoutes />} />
              </Route>
              <Route path="quote-details/:id" element={<QuotePublicDetail />} />
              {/* <Route path="quote-detail/:id" element={<QuotePublicDetail />} /> */}
              <Route path="*" element={<NotFoundpage />} />
            </Routes>
          </Suspense>
          <Toaster position="top-center" />
        </HashRouter>
      </ChakraProvider>
    </>
  );
}

export default App;